import {
  bridgeChannel,
  contentRoutePrefix,
  isPlayerMessage,
  type PlayerMessage,
  type WrapperMessage,
} from '../protocol'

import { readTokenOrigin } from './content-token'
import { createScormApi, type ScormApi } from './scorm-api'

type WrapperWindow = Window & { API?: ScormApi }

/**
 * Resolves a package path against the token's content root, or null when it would leave it.
 * The URL parser folds `..`, encoded dots and backslashes first, so the prefix check sees where
 * the browser would really go. It keeps encoded slashes, which nginx decodes before routing, so
 * those are refused outright.
 */
export function packageUrl(origin: string, token: string, path: string): URL | null {
  const root = new URL(`${contentRoutePrefix}${encodeURIComponent(token)}/`, origin)
  if (path.startsWith('/') || path.includes('\\')) {
    return null
  }
  let url: URL
  try {
    url = new URL(path, root)
  } catch {
    return null
  }
  return url.origin === root.origin
    && url.pathname.startsWith(root.pathname)
    && url.pathname !== root.pathname
    && !/%2f|%5c/i.test(url.pathname)
    ? url
    : null
}

function openPackage(win: WrapperWindow, token: string, message: PlayerMessage): void {
  const url = packageUrl(win.location.origin, token, message.path)
  if (url === null) {
    return
  }

  if (message.type === 'launch-cmi5') {
    for (const [name, value] of Object.entries(message.parameters)) {
      url.searchParams.set(name, value)
    }
  }

  const document = win.document
  document.documentElement.lang = message.language
  document.title = message.title

  const frame = document.createElement('iframe')
  frame.title = message.title
  frame.allow = 'fullscreen; autoplay'
  frame.src = url.href
  document.body.replaceChildren(frame)
}

/**
 * Starts the wrapper page: answers only the player origin its token names, takes one launch from
 * it, and opens the package in a frame below, where SCORM content finds `API` on its parent.
 * Does nothing unless framed with a readable token.
 */
export function startWrapper(win: WrapperWindow): void {
  const token = win.location.hash.slice(1)
  const playerOrigin = readTokenOrigin(token)
  const player = win.parent
  if (playerOrigin === null || player === win) {
    return
  }

  const send = (message: WrapperMessage): void => {
    player.postMessage(message, playerOrigin)
  }

  let launched = false
  win.addEventListener('message', (event: MessageEvent<unknown>) => {
    if (launched || event.origin !== playerOrigin || event.source !== player || !isPlayerMessage(event.data)) {
      return
    }
    launched = true
    if (event.data.type === 'launch-scorm') {
      win.API = createScormApi(event.data.values, send)
    }
    openPackage(win, token, event.data)
  })

  send({ channel: bridgeChannel, type: 'ready' })
}
